/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { type Insight as BaseInsight, registerStandardChartInsight } from '../insightManager';
import { toArray } from '../../util/data';
import {
  findSeriesDataItemInfo,
  getSingletonInsightDvMarkerSeries,
  mergeSeriesDataItem,
  getDataItemValue
} from '../../helper/standardChart';
import {
  findExtend,
  parsePercent,
  handleMultipleSeries,
  handleAnimation,
  isMultipleSeriesWithoutDvMarker
} from '../helper';

type InsightData = {
  /** x 轴指标对应数据集字段 */
  x: string;
  /** y 轴指标对应数据集字段 */
  y: string;
  /** 要标记的 x 轴指标数据值 */
  xValue: string;
  /** 要标记的目标点 y 轴指标数据值(多系列下通过此字段区分series) */
  yCompared: string;
  /** 要标记的对比点 y 轴指标数据值 */
  yCompare?: string;
  /** 线的文本描述 */
  yValueSummary: string;
  /** 点的文本描述 */
  summary: string;
  /** 文本摘要 @see https://datav.iwencai.com/components/paradigm-chart/docs/apis/series/dvMarker/#seriesdvitemstyle */
  textSummary?: unknown;
  /** 对比值线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  line?: unknown;
  /** 点与线之间的参考线 @see https://echarts.apache.org/zh/option.html#series-bar.markLine.data */
  referenceLine?: unknown;
  /** 弱化系列 */
  blurSeries?: unknown;
  /** 开启箭头隐藏的百分比 */
  hideArrowThreshold?: number | string;
};

const type = 'CompareLine';
const INSIGHT_FLAG = `insight_${type}`;

interface Insight extends BaseInsight<InsightData[]> {
  readonly type: typeof type;
}

registerStandardChartInsight<Insight>({
  type,
  filter: option => {
    return (
      toArray(option.xAxis).length > 0 &&
      toArray(option.yAxis).length > 0 &&
      !toArray(option.series).some(item => !['line', 'bar', 'scatter'].includes(item.type))
    );
  },
  clear: {
    onOptionUpdate(option) {
      option.series = toArray(option.series).map(series => {
        series.markLine = series.markLine || {};
        series.markLine.data = (series.markLine.data || []).filter(
          item => item.__insightFlag !== INSIGHT_FLAG
        );

        return series;
      });
      if (option?.legend?.data) {
        option.legend.data = option.legend.data?.map(legendItem => {
          delete (legendItem as { __blur?: number }).__blur;
          return legendItem;
        });
      }
    }
  },
  render: {
    onOptionUpdate(context, option, legendIns) {
      const insight = context.insightOption;
      const { linkLegend = false } = insight;
      const { animationDuration = 0 } = insight;
      const isMultipleSeries = isMultipleSeriesWithoutDvMarker(option);

      function render(data: InsightData) {
        const {
          x,
          y,
          xValue,
          yCompare,
          yCompared,
          yValueSummary,
          summary,
          textSummary: textSummaryOpt = {},
          line: lineOpt = {},
          referenceLine: referenceLineOpt = {},
          blurSeries = {},
          hideArrowThreshold = 0.1
        } = data;
        const blurSeriesOpt = JSON.parse(JSON.stringify(blurSeries));
        const pointDataItemMetaInfo = findSeriesDataItemInfo(option, {
          xField: x,
          yField: y,
          xValue,
          yValue: yCompare
        });
        if (!pointDataItemMetaInfo) {
          return;
        }
        const { xAxisIndex, yAxisIndex, seriesIndex, dataItemIndex } = pointDataItemMetaInfo;
        const series = context.util.data.toArray(option.series)[seriesIndex];
        const extend = findExtend(series.data);
        const tempDataValue = getDataItemValue(series.data[dataItemIndex]);
        // 适配散点图（data中的value为数组）
        const dataValue = (tempDataValue as [number, number])[1] || tempDataValue;
        const comparedDataValue = yCompared;
        const pointDiffRatio =
          Math.abs(+dataValue - +comparedDataValue) / (+extend[1] - +extend[0]);

        series.markLine = series.markLine || {};
        series.markLine.data = series.markLine.data || [];

        const line = {
          ...context.util.data.deepMerge(lineOpt, {
            // ! bugs @see https://github.com/apache/echarts/issues/18433
            symbol: 'none',
            dvSymbolNone: true,
            label: {
              position: 'insideStartTop'
            }
          }),
          // ! name 在该模式下不生效
          name: yValueSummary,
          dvLabelText: yValueSummary,
          yAxis: yCompared,
          __insightFlag: INSIGHT_FLAG
        };
        const referenceLine0 = [
          {
            ...context.util.data.deepMerge(lineOpt, {
              symbol: 'circle',
              emphasis: {
                disabled: true
              }
            }),
            coord: [xValue, yCompared]
          },
          {
            ...context.util.data.deepMerge(lineOpt, {
              symbol: 'none'
            }),
            coord: [xValue, yCompared]
          }
        ];
        (referenceLine0 as unknown as { __insightFlag: string }).__insightFlag = INSIGHT_FLAG;
        const referenceLine1 = [
          {
            ...context.util.data.deepMerge(
              referenceLineOpt,
              {
                symbol:
                  pointDiffRatio < parsePercent(hideArrowThreshold, +extend[1] - +extend[0])
                    ? 'none'
                    : (referenceLineOpt as { symbol: string }).symbol
                    ? (referenceLineOpt as { symbol: string }).symbol
                    : 'arrow'
              },
              { override: true }
            ),
            coord: [xValue, dataValue]
          },
          {
            ...context.util.data.deepMerge(
              referenceLineOpt,
              {
                symbol:
                  pointDiffRatio < parsePercent(hideArrowThreshold, +extend[1] - +extend[0])
                    ? 'none'
                    : (referenceLineOpt as { symbol: string }).symbol
                    ? (referenceLineOpt as { symbol: string }).symbol
                    : 'arrow'
              },
              { override: true }
            ),
            coord: [xValue, yCompared]
          }
        ];
        (referenceLine1 as unknown as { __insightFlag: string }).__insightFlag = INSIGHT_FLAG;

        series.markLine.data.push(line, referenceLine0, referenceLine1);

        const markerSeries = getSingletonInsightDvMarkerSeries(option, { xAxisIndex, yAxisIndex });
        const pointSummary = {
          value: [xValue, dataValue],
          __seriesIndex: seriesIndex,
          itemStyle: context.util.data.deepMerge(
            {
              textStyle: {
                style: {
                  text: summary
                }
              }
            },
            textSummaryOpt
          )
        };
        handleAnimation(series.markLine, animationDuration, markerSeries);

        markerSeries.data.push(pointSummary as unknown as number);

        // * 弱化主图
        if (!option.series[seriesIndex].areaStyle) {
          delete blurSeriesOpt.areaStyle;
        }
        if (isMultipleSeries) {
          // TODO 多系列下柱状图的弱化效果不同
          handleMultipleSeries(option, seriesIndex, legendIns!, blurSeriesOpt, linkLegend);
          return;
        }
        if (series.type === 'bar') {
          series.data = series.data.map((dataItem, index) => {
            if (
              [dataItemIndex].includes(index) ||
              (dataItem as unknown as { __noBlur?: boolean })?.__noBlur
            ) {
              return mergeSeriesDataItem(dataItem, { __noBlur: true });
            }

            return mergeSeriesDataItem(dataItem, blurSeriesOpt);
          });
        }
        if (series.type === 'line') {
          option.series = context.util.data.toArray(option.series);
          option.series[seriesIndex] = context.util.data.deepMerge(series, blurSeriesOpt);
        }
      }

      insight.data.forEach(item => render(item));
    }
  }
});
